import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Slider,
  TextField,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SceneCanvasEditor from '../components/SceneCanvasEditor';
import { ImageFileDropZone } from '../components/flow/components/flowBgAssetsUi';
import { API_BASE_URL } from '../utils/api';

function goBackToScenes(navigate) {
  navigate('/', { state: { adminNavId: 'scenes' } });
}

/**
 * Scene layout must use the original upload. Thumbnails are square (800×800 cover) and crop widescreen art.
 */
function sceneCanvasAssetUrl(doc) {
  return doc?.imageUrl || doc?.thumbnailUrl || '';
}

function idStr(ref) {
  if (!ref) return '';
  if (typeof ref === 'object' && ref._id) return String(ref._id);
  return String(ref);
}

const SCALE_MIN_W = 0.06;
const SCALE_MAX_W = 3;
/** Foreground stays inside the 1×1 logical frame so the canvas does not clip it (overflow: hidden). */
const FG_SCALE_MAX_W = 1;

/** width/height in normalized scene coords (not pixels). Must not be clamped — e.g. 1/0.2 = 5 for a bottom strip. */
function normWidthOverHeight(rect) {
  const r = rect.width / Math.max(rect.height, 1e-9);
  if (!Number.isFinite(r) || r <= 0) return 1;
  return r;
}

/**
 * Old scenes used foregroundRect { x:0,y:0, width:1, height:0.2 } (a bottom strip).
 * A full-screen 16:9 PNG in that strip renders ~722×81 — wrong. Upgrade to full frame once.
 */
function migrateLegacyForegroundStripToFullFrame(fr) {
  const x = Number(fr.x) || 0;
  const y = Number(fr.y) || 0;
  const w = Number(fr.width) || 1;
  const h = Number(fr.height) || 1;
  const isOldDefaultStrip =
    Math.abs(x) < 0.02 &&
    Math.abs(y) < 0.02 &&
    Math.abs(w - 1) < 0.03 &&
    h >= 0.14 &&
    h <= 0.26;
  if (isOldDefaultStrip) {
    return { x: 0, y: 0, width: 1, height: 1 };
  }
  return { x, y, width: w, height: h };
}

/** Uniform scale so width ≤ 1 and height ≤ 1; keeps center. */
function fitRectInsideUnitFrame(rect) {
  let { x, y, width: w, height: h } = rect;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return { ...rect };
  const cx = x + w / 2;
  const cy = y + h / 2;
  const s = Math.min(1 / w, 1 / h, 1);
  if (s >= 1 - 1e-9) return { ...rect };
  w *= s;
  h *= s;
  return { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
}

/** Wider/narrower layer; keeps norm width/height ratio and center. */
function applyWidthKeepingCenter(rect, newWidth) {
  const ar = normWidthOverHeight(rect);
  const w = Math.min(SCALE_MAX_W, Math.max(SCALE_MIN_W, newWidth));
  const h = w / ar;
  if (!Number.isFinite(h) || h <= 0) return { ...rect };
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  return {
    x: cx - w / 2,
    y: cy - h / 2,
    width: w,
    height: h
  };
}

/** Foreground scale: same as above, then force layer inside unit frame (no edge crop from canvas overflow). */
function applyForegroundScale(rect, newWidth) {
  const ar = normWidthOverHeight(rect);
  const w = Math.min(FG_SCALE_MAX_W, Math.max(SCALE_MIN_W, newWidth));
  const h = w / ar;
  if (!Number.isFinite(h) || h <= 0) return { ...rect };
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const next = { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
  return fitRectInsideUnitFrame(next);
}

export default function SceneCanvasPage({ userRole }) {
  const { sceneId } = useParams();
  const navigate = useNavigate();

  const [scene, setScene] = useState(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [flows, setFlows] = useState([]);
  const [flowIdSel, setFlowIdSel] = useState('');
  const [flow2IdSel, setFlow2IdSel] = useState('');
  const [flow3IdSel, setFlow3IdSel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');

  const [bgId, setBgId] = useState('');
  const [fgId, setFgId] = useState('');
  const [backgroundRect, setBackgroundRect] = useState({ x: 0, y: 0, width: 1, height: 1 });
  const [characterRect, setCharacterRect] = useState({ x: 0.25, y: 0.12, width: 0.5, height: 0.88 });
  const [foregroundRect, setForegroundRect] = useState({ x: 0, y: 0, width: 1, height: 1 });

  const bgFileRef = useRef(null);
  const fgFileRef = useRef(null);
  const [bgPick, setBgPick] = useState(null);
  const [fgPick, setFgPick] = useState(null);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [bgRes, chRes, scRes, flRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/backgrounds`),
        fetch(`${API_BASE_URL}/api/characters`),
        fetch(`${API_BASE_URL}/api/scenes/${sceneId}`),
        fetch(`${API_BASE_URL}/api/flows`)
      ]);
      if (!bgRes.ok) throw new Error('Failed to load backgrounds');
      if (!chRes.ok) throw new Error('Failed to load characters');
      if (!scRes.ok) throw new Error('Scene not found');
      if (!flRes.ok) throw new Error('Failed to load flows');
      const [bg, ch, sc, fl] = await Promise.all([bgRes.json(), chRes.json(), scRes.json(), flRes.json()]);
      setBackgrounds(Array.isArray(bg) ? bg : []);
      setCharacters(Array.isArray(ch) ? ch : []);
      setFlows(Array.isArray(fl) ? fl : []);
      setScene(sc);
      setFlowIdSel(idStr(sc.flowId));
      setFlow2IdSel(idStr(sc.flow2Id));
      setFlow3IdSel(idStr(sc.flow3Id));
      setBgId(idStr(sc.backgroundAssetId));
      setFgId(idStr(sc.foregroundAssetId));
      if (sc.backgroundRect) setBackgroundRect(sc.backgroundRect);
      if (sc.characterRect) setCharacterRect(sc.characterRect);
      if (sc.foregroundRect) {
        let fr = migrateLegacyForegroundStripToFullFrame(sc.foregroundRect);
        fr = fr.width > 1 || fr.height > 1 ? fitRectInsideUnitFrame(fr) : fr;
        setForegroundRect(fr);
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [sceneId]);

  useEffect(() => {
    load();
  }, [load]);

  const mergeBgIntoList = (doc) => {
    if (!doc?._id) return;
    setBackgrounds((prev) => {
      const rest = prev.filter((b) => String(b._id) !== String(doc._id));
      return [doc, ...rest];
    });
  };

  const uploadAsset = async (file, slot) => {
    setUploadError('');
    const fd = new FormData();
    fd.append('image', file);
    fd.append('kind', 'background');
    const res = await fetch(`${API_BASE_URL}/api/backgrounds`, { method: 'POST', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Upload failed');
    }
    const doc = await res.json();
    mergeBgIntoList(doc);
    if (slot === 'bg') {
      setBgId(String(doc._id));
      setBgPick(null);
      if (bgFileRef.current) bgFileRef.current.value = '';
    } else {
      setFgId(String(doc._id));
      setFgPick(null);
      if (fgFileRef.current) fgFileRef.current.value = '';
    }
  };

  const handleBgFile = async (file) => {
    setBgPick(file || null);
    if (!file) return;
    try {
      await uploadAsset(file, 'bg');
    } catch (e) {
      setUploadError(e.message);
    }
  };

  const handleFgFile = async (file) => {
    setFgPick(file || null);
    if (!file) return;
    try {
      await uploadAsset(file, 'fg');
    } catch (e) {
      setUploadError(e.message);
    }
  };

  const bgDoc = useMemo(() => {
    if (scene?.backgroundAssetId && typeof scene.backgroundAssetId === 'object' && idStr(scene.backgroundAssetId) === bgId) {
      return scene.backgroundAssetId;
    }
    return backgrounds.find((b) => String(b._id) === String(bgId));
  }, [scene, backgrounds, bgId]);

  const fgDoc = useMemo(() => {
    if (scene?.foregroundAssetId && typeof scene.foregroundAssetId === 'object' && idStr(scene.foregroundAssetId) === fgId) {
      return scene.foregroundAssetId;
    }
    return backgrounds.find((b) => String(b._id) === String(fgId));
  }, [scene, backgrounds, fgId]);

  const charDoc = useMemo(() => {
    const cid = idStr(scene?.characterId);
    if (scene?.characterId && typeof scene.characterId === 'object') return scene.characterId;
    return characters.find((c) => String(c._id) === cid);
  }, [scene, characters]);

  const handleSave = async () => {
    if (!scene) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: scene.name,
        backgroundAssetId: bgId || null,
        foregroundAssetId: fgId || null,
        characterId: idStr(scene.characterId) || null,
        flowId: flowIdSel || null,
        flow2Id: flow2IdSel || null,
        flow3Id: flow3IdSel || null,
        backgroundRect,
        characterRect,
        foregroundRect
      };
      const res = await fetch(`${API_BASE_URL}/api/scenes/${sceneId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || 'Save failed');
      }
      goBackToScenes(navigate);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (userRole !== 'admin' && userRole !== 'member') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Access denied.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box
        sx={{
          width: 280,
          maxWidth: '100%',
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          px: 1,
          py: 1,
          gap: 0.75,
          overflowX: 'hidden',
          overflowY: 'auto',
          minWidth: 0,
          boxSizing: 'border-box'
        }}
      >
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => goBackToScenes(navigate)}
          size="small"
          sx={{
            alignSelf: 'flex-start',
            minHeight: 28,
            py: 0.25,
            px: 0.75,
            fontSize: 11,
            maxWidth: '100%',
            whiteSpace: 'normal',
            textAlign: 'left'
          }}
        >
          Back to list
        </Button>
        <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {loading ? '…' : scene?.name || 'Scene'}
        </Typography>
        {error && (
          <Alert
            severity="error"
            dense
            onClose={() => setError('')}
            sx={{
              py: 0,
              alignItems: 'center',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              '& .MuiAlert-message': { py: 0.25, overflow: 'hidden', wordBreak: 'break-word' }
            }}
          >
            {error}
          </Alert>
        )}
        {uploadError && (
          <Alert
            severity="warning"
            dense
            onClose={() => setUploadError('')}
            sx={{
              py: 0,
              alignItems: 'center',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              '& .MuiAlert-message': { py: 0.25, overflow: 'hidden', wordBreak: 'break-word' }
            }}
          >
            {uploadError}
          </Alert>
        )}
        <Divider sx={{ my: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1, fontSize: 10 }}>
          Flows
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          label="Primary"
          value={flowIdSel}
          onChange={(e) => setFlowIdSel(e.target.value)}
          disabled={loading || !scene}
          sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {flows.map((f) => (
            <MenuItem key={f._id} value={String(f._id)}>
              {f.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Second (same scene)"
          value={flow2IdSel}
          onChange={(e) => setFlow2IdSel(e.target.value)}
          disabled={loading || !scene}
          sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {flows.map((f) => (
            <MenuItem key={f._id} value={String(f._id)}>
              {f.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Third (same scene)"
          value={flow3IdSel}
          onChange={(e) => setFlow3IdSel(e.target.value)}
          disabled={loading || !scene}
          sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {flows.map((f) => (
            <MenuItem key={f._id} value={String(f._id)}>
              {f.name}
            </MenuItem>
          ))}
        </TextField>
        <Divider sx={{ my: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1, fontSize: 10 }}>
          Background
        </Typography>
        <ImageFileDropZone compact file={bgPick} onFile={handleBgFile} inputRef={bgFileRef} disabled={loading || !scene} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1, fontSize: 10 }}>
          Foreground
        </Typography>
        <ImageFileDropZone compact file={fgPick} onFile={handleFgFile} inputRef={fgFileRef} disabled={loading || !scene} />
        <Divider sx={{ my: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1, fontSize: 10 }}>
          Layer scale
        </Typography>
        <Box sx={{ mt: -0.25, width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10, lineHeight: 1 }}>
            Background
          </Typography>
          <Slider
            size="small"
            disabled={loading || !scene}
            min={SCALE_MIN_W}
            max={SCALE_MAX_W}
            step={0.005}
            value={backgroundRect.width}
            onChange={(_, v) => setBackgroundRect((r) => applyWidthKeepingCenter(r, v))}
            valueLabelDisplay="off"
            sx={{ py: 0, my: 0, px: 0.5, boxSizing: 'border-box', '& .MuiSlider-thumb': { width: 12, height: 12 }, '& .MuiSlider-rail': { opacity: 0.35 } }}
          />
        </Box>
        <Box sx={{ mt: -0.5, width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10, lineHeight: 1 }}>
            Character
          </Typography>
          <Slider
            size="small"
            disabled={loading || !scene || !idStr(scene.characterId)}
            min={SCALE_MIN_W}
            max={SCALE_MAX_W}
            step={0.005}
            value={characterRect.width}
            onChange={(_, v) => setCharacterRect((r) => applyWidthKeepingCenter(r, v))}
            valueLabelDisplay="off"
            sx={{ py: 0, my: 0, px: 0.5, boxSizing: 'border-box', '& .MuiSlider-thumb': { width: 12, height: 12 }, '& .MuiSlider-rail': { opacity: 0.35 } }}
          />
        </Box>
        <Box sx={{ mt: -0.5, width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10, lineHeight: 1 }}>
            Foreground
          </Typography>
          <Slider
            size="small"
            disabled={loading || !scene || !fgId}
            min={SCALE_MIN_W}
            max={FG_SCALE_MAX_W}
            step={0.005}
            value={Math.min(foregroundRect.width, FG_SCALE_MAX_W)}
            onChange={(_, v) => setForegroundRect((r) => applyForegroundScale(r, v))}
            valueLabelDisplay="off"
            sx={{ py: 0, my: 0, px: 0.5, boxSizing: 'border-box', '& .MuiSlider-thumb': { width: 12, height: 12 }, '& .MuiSlider-rail': { opacity: 0.35 } }}
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 4 }} />
        <Button
          variant="contained"
          fullWidth
          size="small"
          startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
          onClick={handleSave}
          disabled={loading || !scene || saving}
          sx={{ minHeight: 30, py: 0.5, fontSize: 12 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : !scene ? (
          <Typography color="text.secondary">Scene not found</Typography>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
            <SceneCanvasEditor
              backgroundRect={backgroundRect}
              setBackgroundRect={setBackgroundRect}
              characterRect={characterRect}
              setCharacterRect={setCharacterRect}
              foregroundRect={foregroundRect}
              setForegroundRect={setForegroundRect}
              bgThumb={sceneCanvasAssetUrl(bgDoc)}
              fgThumb={sceneCanvasAssetUrl(fgDoc)}
              charDoc={charDoc}
              hasBackground
              hasCharacter={Boolean(idStr(scene.characterId))}
              hasForeground={Boolean(fgId)}
              maxWidth={1600}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
